import { EffectCallback, useCallback, useEffect, useId, useRef, useState } from 'react';

import { AxiosError, AxiosResponse } from 'axios';
import { useNavigate } from 'react-router';

import ErrorModel from '@telialt/models/src/ErrorModel';

import { datadogLogger } from './datadog/datadogLogger';
import { DDComponent } from './datadog/setup';
import { isEmpty } from './helpers';
import { isMobileRes } from './responsive';
import { UserData } from './SSO/model';

export const useSubmit = (
    isSubmitting: boolean,
    error: ErrorModel | AxiosError,
    doneCallback: () => void,
    failCallback?: () => void,
) => {
    const [isSubmitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (!isEmpty(error)) {
            if (failCallback) {
                failCallback();
            }

            return setSubmitted(false);
        }

        if (isSubmitted && !isSubmitting) {
            doneCallback();
        }
    }, [isSubmitting]);

    return () => setSubmitted(true);
};

export const useResolution = (checkIsResolution: () => boolean = isMobileRes) => {
    const [isResolution, setResolution] = useState(checkIsResolution());

    const updateResolution = () => setResolution(checkIsResolution());

    useEffect(() => {
        window.addEventListener('resize', updateResolution);

        return () => window.removeEventListener('resize', updateResolution);
    }, []);

    return isResolution;
};

/**
 * @deprecated use `useEffect` instead
 */
export const useDidMount = (callback: EffectCallback) => {
    useEffect(callback, []);
};

const IGNORED_OBJECT_KEYS = ['then', 'toJSON'];
// TODO: to be checked with https://teliaprojects.telia.lt/browse/LTDAT-4891
const EXCLUDED_MESSAGES = ['tariff.roaming', 'tariff.international', 'ss.services.mobility.plan.benefit'];

export const withMissingKeyLogging = (object: Record<string, string>, shouldUseKeyAsDefault = true) =>
    new Proxy(object, {
        get: (target, key: string): string => {
            if (target.hasOwnProperty(key)) {
                return target[key];
            } else {
                if (IGNORED_OBJECT_KEYS.includes(key)) {
                    return '';
                }

                if (
                    !EXCLUDED_MESSAGES.find((excludedMessageKey) => key.includes(excludedMessageKey)) &&
                    process.env.NODE_ENV !== 'test'
                ) {
                    const stack = new Error().stack;
                    // eslint-disable-next-line no-console
                    console.error(`MissingMessageError: Missing message for key ${key}`);
                    const datadogLogs = datadogLogger();
                    datadogLogs.error(`MissingMessageError: Missing message for key ${key}`, {
                        error: { stack },
                        component: DDComponent.Messages,
                    });
                }

                return shouldUseKeyAsDefault ? key : '';
            }
        },
    });

export const useSsoUserData = (): UserData => {
    return window.TC?.sso?.getUserData() || null;
};

type FunctionWithPromise = (...args: any) => Promise<any>;
type Result<Action extends FunctionWithPromise> =
    ReturnType<Action> extends PromiseLike<AxiosResponse<infer U>> ? U : never;
type ResultAction<Action extends FunctionWithPromise> = (...args: Parameters<Action>) => Promise<State<Action>>;

type State<Action extends FunctionWithPromise> = {
    result: Result<Action> | undefined;
    error: any;
    isLoading: boolean;
    isLoaded: boolean;
};

export const useApi = <Action extends FunctionWithPromise>(
    asyncAction: Action,
): [ResultAction<Action>, Result<Action> | undefined, any, boolean, boolean] => {
    const isMounted = useRef(true);
    const [{ result, error, isLoading, isLoaded }, setState] = useState<State<Action>>({
        result: undefined,
        error: null,
        isLoading: false,
        isLoaded: false,
    });

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const wrappedAction = useCallback(
        async (...args: Parameters<Action>) => {
            setState((prevState) => ({
                result: prevState.result,
                error: null,
                isLoading: true,
                isLoaded: false,
            }));

            try {
                const response = await asyncAction(...args);
                const updatedState = {
                    result: response.data,
                    error: null,
                    isLoading: false,
                    isLoaded: true,
                };

                if (isMounted.current) {
                    setState(updatedState);
                }

                return updatedState;
            } catch (e) {
                const { response } = e;
                const updatedState = {
                    result: undefined,
                    error: response ? { status: response.status, ...response.data } : { ...e, type: 'ConnectionError' },
                    isLoading: false,
                    isLoaded: true,
                };

                if (isMounted.current) {
                    setState(updatedState);
                }

                return updatedState;
            }
        },
        [asyncAction],
    );

    return [wrappedAction, result, error, isLoading, isLoaded];
};

export const useNavigateBack = () => {
    const navigate = useNavigate();
    const prevParam = 'prev';
    const queryParams = new URLSearchParams(location.search);

    if (queryParams && queryParams.has(prevParam)) {
        const url = new URL(queryParams.get(prevParam) || '', location.origin);

        return () => location.assign(url.pathname);
    } else {
        return () => navigate(-1);
    }
};

export const usePrevious = <T>(value: T) => {
    const ref = useRef<T>(null);
    useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref.current;
};

export const useComponentId = (prefix: string = '') => {
    return prefix + useId().replaceAll(':', '-');
};
